/**
 * IDE Status Subcommand
 * Show detected IDEs and their configuration status
 */
import { Command } from 'commander';
import { IdeDetectionService } from '../../services/ide-detection.service.js';

/**
 * Create status subcommand for IDE group
 * Shows which IDEs are detected and configured
 */
export function createStatusSubcommand(): Command {
    const cmd = new Command('status')
        .description('Show detected IDEs and their configuration status')
        .option('--json', 'Output as JSON')
        .option('--suggestions', 'Show IDE suggestions based on project structure')
        .action(async (options: {
            json?: boolean;
            suggestions?: boolean;
        }) => {
            await runStatus(options);
        });

    return cmd;
}

async function runStatus(options: { json?: boolean; suggestions?: boolean }) {
    const service = new IdeDetectionService();

    if (options.json) {
        const summary = await service.getSummary();
        console.log(JSON.stringify(summary, null, 2));
        return;
    }

    console.log('\n🔍 IDE Detection Status\n');

    const summary = await service.getSummary();

    if (summary.active.length === 0) {
        console.log('⚠️  No active IDEs detected\n');
        console.log('💡 Tips:');
        console.log('   • Run "jai1 rules apply" to configure IDE rules');
        console.log('   • Create rules in .cursor/rules/, .windsurf/rules/, etc.');
        console.log('   • Create AGENTS.md for universal IDE support\n');
    } else {
        console.log(`📊 Found ${summary.active.length} active IDE(s):\n`);

        // Show primary IDE
        if (summary.primary) {
            const confidence = getConfidenceEmoji(summary.primary.confidence);
            console.log(`🎯 Primary IDE: ${summary.primary.name} ${confidence}\n`);
        }

        // Show all active IDEs
        for (const ide of summary.active) {
            const confidence = getConfidenceEmoji(ide.confidence);
            const isPrimary = summary.primary?.id === ide.id;
            const marker = isPrimary ? '🎯' : '  ';

            console.log(`${marker} ${ide.name} ${confidence}`);

            if (ide.hasRules) {
                console.log(`     Rules: ${ide.ruleCount} file(s)`);
            }

            if (ide.hasWorkflows) {
                console.log(`     Workflows: ${ide.workflowCount} file(s)`);
            }

            const format = service.getIdeFormat(ide.id);
            if (format) {
                if (ide.id === 'agentsmd') {
                    console.log(`     Location: AGENTS.md`);
                } else if (ide.id === 'gemini') {
                    console.log(`     Location: GEMINI.md`);
                } else if (ide.id === 'opencode') {
                    console.log(`     Location: AGENTS.md + .opencode/command/`);
                } else if (ide.id === 'codex') {
                    console.log(`     Location: AGENTS.md + .agents/skills/ + .codex/config.toml`);
                } else {
                    console.log(`     Location: ${format.rulesPath}/`);
                }
            }
            console.log('');
        }
    }

    // Show inactive IDEs
    if (summary.inactive.length > 0) {
        console.log(`📋 Available IDEs (not configured):\n`);
        for (const ideId of summary.inactive) {
            const format = service.getIdeFormat(ideId);
            if (format) {
                console.log(`   • ${format.name} - ${format.description}`);
            }
        }
        console.log('');
    }

    // Show suggestions
    if (options.suggestions) {
        const suggestions = await service.suggestIdes();

        if (suggestions.length > 0) {
            console.log('💡 Suggested IDEs for this project:\n');

            for (const suggestion of suggestions) {
                const priorityEmoji = suggestion.priority === 'high' ? '⭐' :
                                     suggestion.priority === 'medium' ? '🔸' : '▫️';

                console.log(`${priorityEmoji} ${suggestion.name}`);
                console.log(`   Reason: ${suggestion.reason}\n`);
            }
        }
    }

    // Show next steps
    console.log('📝 Commands:\n');
    console.log('   • "jai1 rules apply" - Configure rules for IDEs');
    console.log('   • "jai1 rules sync" - Regenerate IDE outputs');
    console.log('   • "jai1 ide sync" - Sync .jai1/ content to IDEs');
    console.log('   • "jai1 ide status --suggestions" - Get IDE recommendations\n');
}

/**
 * Get emoji for confidence level
 */
function getConfidenceEmoji(confidence: 'high' | 'medium' | 'low'): string {
    switch (confidence) {
        case 'high':
            return '🟢';
        case 'medium':
            return '🟡';
        case 'low':
            return '🔴';
        default:
            return '⚪';
    }
}
